import { borderRadius, borderWidth, paddingSize, marginSize, borderColor } from '../../Utilities/utils/Theming';
import { IDummyProps } from './types';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { focusRing } from '../..//Themes/base/focusRing';

const backgroundColors: { [key: string]: string } = {
	indigo: 'var(--epods--color-bg-tag-indigo)',
	cyan: 'var(--epods--color-bg-tag-cyan)',
	'blue-grey': 'var(--epods--color-bg-tag-bluegrey)',
	outline: 'var(--epods--color-bg-tag-outline)',
	'outline-indigo': 'var(--epods--color-bg-tag-indigo)',
	'outline-cyan': 'var(--epods--color-bg-tag-cyan)',
	'outline-blue-grey': 'var(--epods--color-bg-tag-bluegrey)',
};
const borderColors: { [key: string]: string } = {
	indigo: 'var(--epods--color-border-tag)',
	cyan: 'var(--epods--color-border-tag)',
	'blue-grey': 'var(--epods--color-border-tag)',
	outline: 'var(--epods--color-border-tag-outline)',
	'outline-indigo': 'var(--epods--color-border-tag-outline)',
	'outline-cyan': 'var(--epods--color-border-tag-outline)',
	'outline-blue-grey': 'var(--epods--color-border-tag-outline)',
};
const closableHoverColors: { [key: string]: string } = {
	indigo: 'var(--epods--color-bg-tag-indigo-hover)',
	cyan: 'var(--epods--color-bg-tag-cyan-hover)',
	'blue-grey': 'var(--epods--color-bg-tag-bluegrey-hover)',
	outline: 'var(--epods--color-bg-tag-outline-hover)',
	'outline-indigo': 'var(--epods--color-bg-tag-indigo-hover)',
	'outline-cyan': 'var(--epods--color-bg-tag-cyan-hover)',
	'outline-blue-grey': 'var(--epods--color-bg-tag-bluegrey-hover)',
};
const closableClickColors: { [key: string]: string } = {
	indigo: 'var(--epods--color-bg-tag-indigo-active)',
	cyan: 'var(--epods--color-bg-tag-cyan-active)',
	'blue-grey': 'var(--epods--color-bg-tag-bluegrey-active)',
	outline: 'var(--epods--color-bg-tag-outline-active)',
	'outline-indigo': 'var(--epods--color-bg-tag-indigo-active)',
	'outline-cyan': 'var(--epods--color-bg-tag-cyan-active)',
	'outline-blue-grey': 'var(--epods--color-bg-tag-bluegrey-active)',
};

export const getTextColor = (theme: string) => {
	if (theme === 'outline') return 'var(--epods--color-text-tag-outline)';

	return 'var(--epods--color-text-tag)';
};

export const getDisabledBackgroundColor = (theme: string) => {
	if (theme === 'outline' || theme === 'outline-indigo' || theme === 'outline-cyan' || theme === 'outline-blue-grey')
		return 'var(--epods--color-bg-tag-outline-disabled)';

	return 'var(--epods--color-bg-tag-disabled)';
};

export const getDisabledTextColor = (theme: string) => {
	if (theme === 'outline' || theme === 'outline-indigo' || theme === 'outline-cyan' || theme === 'outline-blue-grey')
		return 'var(--epods--color-text-tag-outline-disabled)';

	return 'var(--epods--color-text-tag-disabled)';
};

export const getDisabledBorderColor = (theme: string) => {
	if (theme === 'outline' || theme === 'outline-indigo' || theme === 'outline-cyan' || theme === 'outline-blue-grey')
		return 'var(--epods--color-border-tag-outline-disabled)';

	return 'var(--epods--color-border-tag-disabled)';
};

export const tagDefaultStyles = (props: IDummyProps, size: string) => {
	const { theme } = props;
	const _hover = props.hoverColor ? { backgroundColor: props.hoverColor } : {};
	const _active = props.activeColor ? { backgroundColor: props.activeColor } : {};

	const BG = {
		...borderWidth('0px'),
		backgroundColor: props.disabled
			? getDisabledBackgroundColor(theme)
			: props.customColor || backgroundColors[theme],
		':hover': {
			boxShadow: 'none',
			..._hover,
		},
		':active': {
			..._active,
		},
	};

	return {
		...borderRadius('var(--epods--border-radius-tag)'),
		height: size === 'default' ? 'var(--epods--tag-default-height)' : 'var(--epods--tag-compact-height)',
		paddingRight: props.closeable || props.closeable === undefined ? 0 : 'var(--epods--space-inset-s)',
		...BG,
	};
};

export const tagOutlinedStyles = (props: IDummyProps, $theme: typeof Theme) => {
	const { theme } = props;

	return {
		...borderWidth('1px'),
		...borderColor(
			props.disabled
				? 'white' //$theme.tag.tagOutlineBorderDisabledColor
				: props.customColor || $theme.tag.tagOutlineTextColor,
		),
		backgroundColor: props.disabled
			? getDisabledBackgroundColor(theme)
			: props.customColor || backgroundColors[theme],
	};
};

export const tagActionStyles = (
	props: IDummyProps,
	$variant: string,
	$theme: typeof Theme,
	$isFocused: boolean,
	size: string,
) => {
	const { theme } = props; // theme variants

	const BG = {
		backgroundColor: props.disabled
			? getDisabledBackgroundColor(theme)
			: props.customColor || backgroundColors[theme],
		':hover': {
			backgroundColor: props.disabled
				? getDisabledBackgroundColor(theme)
				: props.hoverColor || closableHoverColors[theme],
			borderLeftColor: props.disabled ? getDisabledBackgroundColor(theme) : borderColors[theme],
		},
		':active': {
			backgroundColor: props.disabled ? getDisabledBackgroundColor(theme) : closableClickColors[theme],
			borderLeftColor: props.disabled ? getDisabledBackgroundColor(theme) : borderColors[theme],
		},
		':focus': focusRing,
	};
	return {
		width: 'var(--epods--tag-action-close-width)',
		height: size === 'default' ? 'var(--epods--tag-default-height)' : 'var(--epods--tag-compact-height)',
		display: 'flex',
		justifyContent: 'center',
		...paddingSize(
			'0',
			'var(--epods--tag-action-close-padding-right)',
			'0',
			'var(--epods--tag-action-close-padding-left)',
		),
		...marginSize('0', '0', '0', 'var(--epods--space-inset-xs2)'),
		borderLeftWidth:
			props.theme === 'outline' ||
			props.theme === 'outline-indigo' ||
			props.theme === 'outline-cyan' ||
			props.theme === 'outline-blue-grey'
				? '1px'
				: '0',
		borderLeftStyle:
			props.theme === 'outline' ||
			props.theme === 'outline-indigo' ||
			props.theme === 'outline-cyan' ||
			props.theme === 'outline-blue-grey'
				? 'solid'
				: 'unset',
		borderLeftColor: props.disabled ? getDisabledBorderColor(theme) : backgroundColors[theme],
		borderTopWidth:
			props.theme === 'outline' ||
			props.theme === 'outline-indigo' ||
			props.theme === 'outline-cyan' ||
			props.theme === 'outline-blue-grey'
				? '1px'
				: '0',
		borderTopStyle:
			props.theme === 'outline' ||
			props.theme === 'outline-indigo' ||
			props.theme === 'outline-cyan' ||
			props.theme === 'outline-blue-grey'
				? 'solid'
				: 'unset',
		borderTopColor: props.disabled ? getDisabledBorderColor(theme) : borderColors[theme],
		borderBottomWidth:
			props.theme === 'outline' ||
			props.theme === 'outline-indigo' ||
			props.theme === 'outline-cyan' ||
			props.theme === 'outline-blue-grey'
				? '1px'
				: '0',
		borderBottomStyle:
			props.theme === 'outline' ||
			props.theme === 'outline-indigo' ||
			props.theme === 'outline-cyan' ||
			props.theme === 'outline-blue-grey'
				? 'solid'
				: 'unset',
		borderBottomColor: props.disabled ? getDisabledBorderColor(theme) : borderColors[theme],
		...BG,
		...($isFocused ? focusRing : {}),
	};
};

export const tagTextStyles = (props: IDummyProps, $theme: typeof Theme, size: string) => {
	const { theme } = props;

	return {
		lineHeight:
			size === 'default' ? 'var(--epods--tag-default-line-height)' : 'var(--epods--tag-compact-line-height)',
		fontSize: size === 'default' ? 'var(--epods--tag-default-font-size)' : 'var(--epods--tag-compact-font-size)',
		color: props.disabled ? getDisabledTextColor(theme) : props.textColor || getTextColor(theme),
	};
};
